'use client';

import React, { forwardRef } from 'react';
import NextLink from 'next/link';
import { cn } from '@/components/ui/_lib/cn';
import {
  buttonLinkClass,
  type ButtonVariantProp,
} from '@/components/ui/_lib/buttonVariants';
import Anchor, { type AnchorProps } from './Anchor';

export type LinksProps = AnchorProps & {
  href: string;
  external?: boolean;
  btn?: ButtonVariantProp;
  disabled?: boolean;
  /** Native tooltip (`title` attribute) on the anchor. */
  title?: string;
};

const Links = forwardRef<HTMLAnchorElement, LinksProps>(
  (
    {
      href,
      external,
      className,
      children,
      disabled,
      btn,
      skeleton,
      skeletonMaxWaitMs,
      skeletonLines,
      title,
      ...props
    },
    ref,
  ) => {
    const isExternal = external || href.startsWith('http');
    const linkClass = cn(
      buttonLinkClass(btn, className),
      disabled && 'pointer-events-none opacity-50',
    );

    if (isExternal) {
      return (
        <Anchor
          ref={ref}
          href={href}
          className={linkClass}
          target="_blank"
          rel="noopener noreferrer"
          aria-disabled={disabled}
          title={title}
          skeleton={skeleton}
          skeletonMaxWaitMs={skeletonMaxWaitMs}
          skeletonLines={skeletonLines}
          {...props}
        >
          {children}
        </Anchor>
      );
    }

    return (
      <NextLink
        ref={ref}
        href={disabled ? '#' : href}
        className={linkClass}
        aria-disabled={disabled}
        title={title}
        {...props}
      >
        {children}
      </NextLink>
    );
  },
);

Links.displayName = 'Links';

export default Links;
